/**
 * CSV download and the UTM link builder — two small pure things the analytics
 * screen leans on, kept out of the components so they can be tested.
 */

/** RFC 4180 quoting: everything quoted, embedded quotes doubled. */
const cell = (value: unknown): string => `"${String(value ?? '').replaceAll('"', '""')}"`;

/**
 * Rows to a CSV string.
 *
 * A BOM is prepended because the overwhelmingly likely destination is Excel,
 * which without one reads UTF-8 as the local codepage and turns every accented
 * city name into mojibake — and the operator then reports it as our bug.
 */
/** Written as an escape, not the literal character: a zero-width byte in the
 *  source is invisible to every reviewer and to most editors. */
const BOM = '\ufeff';

export const toCsv = (headers: string[], rows: unknown[][]): string =>
  `${BOM}${[headers.map(cell).join(','), ...rows.map((row) => row.map(cell).join(','))].join('\r\n')}`;

/** Hand the browser a file. Separated from `toCsv` so the format can be tested. */
export const downloadCsv = (filename: string, headers: string[], rows: unknown[][]): void => {
  const blob = new Blob([toCsv(headers, rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export interface UtmParts {
  url: string;
  source: string;
  medium: string;
  campaign: string;
  content?: string;
  term?: string;
}

/**
 * A tagged link.
 *
 * The whole point of the builder is that `utm_source` gets typed three
 * different ways by three different people and the campaign report then shows
 * Google, google and Google Ads as three sources. So the values are lower-cased
 * and their spaces turned into hyphens here rather than being left to whoever
 * is pasting into an email — which is also why the preview shows the tidied
 * value and not what was typed.
 *
 * Existing parameters on the URL are preserved; existing utm_* are replaced.
 * Returns null when there is nothing usable to build from.
 */
export const buildUtmUrl = (parts: UtmParts): string | null => {
  const raw = parts.url.trim();
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const tidy = (value: string | undefined): string =>
    (value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9._~-]/g, '');
  const set = (key: string, value: string | undefined) => {
    const clean = tidy(value);
    if (clean) parsed.searchParams.set(key, clean);
    else parsed.searchParams.delete(key);
  };
  set('utm_source', parts.source);
  set('utm_medium', parts.medium);
  set('utm_campaign', parts.campaign);
  set('utm_content', parts.content);
  set('utm_term', parts.term);
  if (!parsed.searchParams.get('utm_source')) return null;
  return parsed.toString();
};
